import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactPlayer from 'react-player';
import moment from 'moment';
import { Col, Row } from "reactstrap";
import "./cctvViewPlayer.css";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CropFreeIcon from '@mui/icons-material/CropFree';
import { VolumeOff } from '@mui/icons-material';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import Stepper from '../../multi-step/stepProgress';
// import TimelineBar from '../../progressScale';
import PushPinIcon from '@mui/icons-material/PushPin';
import AutorenewIcon from '@mui/icons-material/Autorenew';
// import SlowMotionVideoIcon from '@mui/icons-material/SlowMotionVideo';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/StopCircle';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { MultiVideoPlayerProps, VideoSegment } from '../../../utils/types';
import ProgressScale from '../../video-player/progressScale';
import showToast from '../../../utils/toast';
import { debounce } from 'lodash';
import { colors } from '../../../constants/colors';
import { ArrowBack } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveIndex } from '../../../redux/reducers/service-reducer';

const MultiVideoPlayerLiveCctv: React.FC<MultiVideoPlayerProps> = ({
    videoSegments,
    timeFilterData,
    playVideoTime,
    onTimeChange = () => { },
    isSDCard = true
}) => {
    const [currentTime, setCurrentTime] = useState<number>(playVideoTime);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    // const [volume, setVolume] = useState<number>(0.5);
    const [volume] = useState<number>(0.5);
    const [muted, setMuted] = useState<boolean>(true);
    const [videoUrls, setVideoUrls] = useState<Record<string, string>>({});
    const [seekTo, setSeekTo] = useState<Record<string, number>>({});
    const [lastProgrammaticUpdate, setLastProgrammaticUpdate] = useState<number>(0);
    const [targetTime, setTargetTime] = useState<number | null>(null);
    const [isJumping, setIsJumping] = useState<boolean>(false);
    const [videoLoading, setVideoLoading] = useState<Record<string, boolean>>({});
    const [videoErrors, setVideoErrors] = useState<Record<string, string | null>>({});
    const [selectedCamera, setSelectedCamera] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const videoContainerRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [isHovered, setIsHovered] = useState(false);
    const [videoRotation, setVideoRotation] = useState<{ [key: string]: number }>({});
    const [cameraOrder, setCameraOrder] = useState<number[]>(videoSegments?.map((_, index) => index));
    const [playbackRate, setPlaybackRate] = useState(1);
    const [showSpeedOptions, setShowSpeedOptions] = useState(false);
    const videoPlaySec = [0.5, 1, 1.5, 2];
    const videoSectionRef = useRef(null);
    const playerRefs = useRef<{ [key: string]: ReactPlayer | null }>({});
    const [recordingState, setRecordingState] = useState<{ [key: string]: boolean }>({});
    const [recordedUrls, setRecordedUrls] = useState<{ [key: string]: string | null }>({});
    const recorderRefs = useRef<{ [key: string]: MediaRecorder | null }>({});
    const chunksRefs = useRef<{ [key: string]: Blob[] }>({});
    const [recordingTimes, setRecordingTimes] = useState<{ [key: string]: number }>({});
    const intervalRefs = useRef<{ [key: string]: NodeJS.Timeout | null }>({});
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const bufferTimeouts = useRef<Record<string, NodeJS.Timeout | null>>({});
    const [isScrolled, setIsScrolled] = useState(false)
    const [liveVideoSegments, setLiveVideoSegments] = useState(videoSegments);
    const initializeVideoStates = useMemo(() => {
        const initialUrls: Record<string, string> = {};
        const initialSeekTo: Record<string, number> = {};
        const initialLoading: Record<string, boolean> = {};
        const initialErrors: Record<string, string | null> = {};
        videoSegments.forEach((_, index) => {
            const cameraKey = `video-${index}`;
            initialUrls[cameraKey] = '';
            initialSeekTo[cameraKey] = 0;
            initialLoading[cameraKey] = true; // Start with loading true for initial load
            initialErrors[cameraKey] = null;
            bufferTimeouts.current[cameraKey] = null;
        });
        return { initialUrls, initialSeekTo, initialLoading, initialErrors };
    }, [videoSegments]);
    useEffect(() => {
        setVideoUrls(initializeVideoStates.initialUrls);
        setSeekTo(initializeVideoStates.initialSeekTo);
        setVideoLoading(initializeVideoStates.initialLoading);
        setVideoErrors(initializeVideoStates.initialErrors);
    }, [initializeVideoStates]);
    useEffect(() => {
        updateVideoSources(playVideoTime, true);
    }, []);
    const timeToSeconds = useCallback((timeStr: string, useSegmentDate: boolean = true): number => {
        const time = moment(timeStr, 'YYYY-MM-DD HH:mm:ss:ss');
        if (useSegmentDate) {
            const referenceDate = moment(videoSegments[0]?.[0]?.start_time || '2025-01-01', 'YYYY-MM-DD');
            time.set({
                year: referenceDate.year(),
                month: referenceDate.month(),
                date: referenceDate.date(),
            });
        }
        const seconds = time.hours() * 3600 + time.minutes() * 60 + time.seconds();
        return seconds >= 0 && seconds < 86400 ? seconds : 0;
    }, [videoSegments]);
    const segmentCache = useRef<Map<string, VideoSegment | undefined>>(new Map());
    const findVideoSegment = useCallback((time: number, cameraIndex: number): VideoSegment | undefined => {
        const cacheKey = `${cameraIndex}_${Math.floor(time)}`;
        if (segmentCache.current.has(cacheKey)) {
            return segmentCache.current.get(cacheKey);
        }
        const segments = liveVideoSegments[cameraIndex] || [];
        const segment = segments.find(segment => {
            return time >= segment.start && time < segment.end;
        });
        segmentCache.current.set(cacheKey, segment);
        return segment;
    }, [videoSegments]);
    const hasSegmentAtTime = useCallback((time: number) => {
        return videoSegments.some((_, index) => findVideoSegment(time, index));
    }, [videoSegments, findVideoSegment]);
    const findNearestValidTime = useCallback((time: number, direction: 'forward' | 'backward' = 'forward') => {
        let nearest = time;
        const step = direction === 'forward' ? 1 : -1;
        while (!hasSegmentAtTime(nearest) && nearest >= 0 && nearest < 86400) {
            nearest += step;
        }
        return nearest;
    }, [hasSegmentAtTime]);
    const updateVideoSources = useCallback(
        (time: number, isProgrammatic: boolean = false) => {
            const newUrls: Record<string, string> = {};
            const newSeekTo: Record<string, number> = {};
            const newLoading = { ...videoLoading };
            const newErrors = { ...videoErrors };
            // For each camera
            videoSegments.forEach((segments, index) => {
                const cameraKey = `video-${index}`;
                // Find the segment that includes the current time
                let segment = segments.find(
                    (seg: any) => time >= seg.start && time < seg.end
                );
                newErrors[cameraKey] = null;
                if (segment) {
                    // Found valid segment
                    const newUrl = segment.specific_file_url;
                    newUrls[cameraKey] = newUrl;
                    const offset = time - segment.start;
                    newSeekTo[cameraKey] = offset >= 0 ? offset : 0;
                } else {
                    // No segment found
                    newUrls[cameraKey] = "";
                    newSeekTo[cameraKey] = 0;
                    newLoading[cameraKey] = false;
                }
            });
            // Apply updates
            setVideoUrls(newUrls);
            setSeekTo(newSeekTo);
            setVideoLoading(newLoading);
            setVideoErrors(newErrors);
            // Seek handling
            const shouldSeek = (key: string, seekTime: number) =>
                seekTime > 0 && playerRefs.current[key];
            Object.entries(newSeekTo).forEach(([cameraKey, seekTime]) => {
                const player = playerRefs.current[cameraKey];
                if (player && shouldSeek(cameraKey, seekTime)) {
                    player.seekTo(seekTime, "seconds");
                }
            });
            if (isProgrammatic) setLastProgrammaticUpdate(Date.now());
        },
        [videoSegments, videoLoading, videoErrors, playerRefs, videoUrls]
    );
    const debouncedHandleTimelineChange = useMemo(() =>
        debounce(() => {
            setIsScrolled(true);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                setIsScrolled(false);
            }, 5000);
        }, 500),
        []
    );

    useEffect(() => {
        setLiveVideoSegments(videoSegments);
    }, [videoSegments]);

    const getLiveEdge = useCallback(() => {
        let maxEnd = 0;
        liveVideoSegments.forEach(cameraSegments => {
            cameraSegments.forEach(seg => {
                if (seg.end > maxEnd) maxEnd = seg.end;
            });
        });
        return maxEnd;
    }, [liveVideoSegments]);

    // const handleTimelineChange = useCallback(
    //     (seconds: number) => {
    //         console.log("handleTimelineChange", seconds);
    //         debouncedHandleTimelineChange()
    //         const now = Date.now();
    //         if (now - lastProgrammaticUpdate < 500 || (targetTime !== null && Math.abs(seconds - targetTime) > 1)) {
    //             return;
    //         }
    //         if (!hasSegmentAtTime(seconds)) {
    //             const nearestTime = findNearestValidTime(seconds);
    //             seconds = nearestTime;
    //         }
    //         setCurrentTime(seconds);
    //         setTargetTime(null);
    //         updateVideoSources(seconds);
    //         onTimeChange(seconds);
    //     },
    //     [lastProgrammaticUpdate, targetTime, updateVideoSources, onTimeChange, hasSegmentAtTime, findNearestValidTime]
    // );

    const handleTimelineChange = useCallback(
        (seconds: number) => {
            debouncedHandleTimelineChange();

            const liveEdge = getLiveEdge();

            // ✅ USER DRAGGED TO LAST → LIVE MODE
            if (seconds >= liveEdge - 1) {
                setIsPlaying(true);
                setCurrentTime(liveEdge);
                updateVideoSources(liveEdge, true);
                onTimeChange(liveEdge);
                return;
            }

            if (!hasSegmentAtTime(seconds)) {
                seconds = findNearestValidTime(seconds);
            }

            setCurrentTime(seconds);
            updateVideoSources(seconds);
            onTimeChange(seconds);
        },
        [
            debouncedHandleTimelineChange,
            getLiveEdge,
            hasSegmentAtTime,
            findNearestValidTime,
            updateVideoSources,
            onTimeChange
        ]
    );

    const createNextLiveSegment = (lastSeg: VideoSegment): VideoSegment => {
        const newStart = lastSeg.end - 40; // overlap buffer
        const newEnd = newStart + 60;

        return {
            start_time: moment.utc(newStart * 1000).format("HH:mm:ss"),
            end_time: moment.utc(newEnd * 1000).format("HH:mm:ss"),
            specific_file_url: lastSeg.specific_file_url.replace(
                /\/\d{6}\//,
                `/${moment.utc(newStart * 1000).format("HHmmss")}/`
            ),
            start: newStart,
            end: newEnd
        };
    };


    const jumpToTime = useCallback(
        (startTime: string) => {
            console.log("jumpToTime", startTime);
            if (isJumping) return;
            setIsJumping(true);
            let seconds = timeToSeconds(startTime, false);
            if (!hasSegmentAtTime(seconds)) {
                seconds = findNearestValidTime(seconds);
            }
            setTargetTime(seconds);
            setCurrentTime(seconds);
            updateVideoSources(seconds, true);
            setIsPlaying(false);
            onTimeChange(seconds);
            setTimeout(() => setIsJumping(false), 100);
        },
        [isJumping, timeToSeconds, updateVideoSources, onTimeChange, hasSegmentAtTime, findNearestValidTime]
    );
    const isAnyVideoLoading = useMemo(() => {
        return Object.values(videoLoading).some(Boolean);
    }, [videoLoading]);
    useEffect(() => {
        let interval: NodeJS.Timeout | undefined;
        if (isPlaying && !isAnyVideoLoading) {
            interval = setInterval(() => {
                const nextTime = currentTime + 1;
                const liveEdge = getLiveEdge();

                // ✅ HIT LIVE EDGE → APPEND NEW SEGMENT
                if (nextTime >= liveEdge - 1) {
                    setLiveVideoSegments(prev =>
                        prev.map(camSegments => {
                            const lastSeg = camSegments[camSegments.length - 1];
                            const newSeg = createNextLiveSegment(lastSeg);
                            return [...camSegments, newSeg];
                        })
                    );

                    setCurrentTime(liveEdge);
                    updateVideoSources(liveEdge, true);
                    onTimeChange(liveEdge);
                    return;
                }

                setCurrentTime(nextTime);
                updateVideoSources(nextTime, true);
                onTimeChange(nextTime);
            }, 1000);

        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isPlaying, playbackRate, updateVideoSources, videoSegments, findVideoSegment, onTimeChange, currentTime, isAnyVideoLoading]);
    const handleReady = useCallback(
        (cameraKey: string) => {
            if (bufferTimeouts.current[cameraKey] !== null) {
                clearTimeout(bufferTimeouts.current[cameraKey]!);
                bufferTimeouts.current[cameraKey] = null;
            }
            setVideoLoading(prev => ({ ...prev, [cameraKey]: false }));
            const player = playerRefs.current[cameraKey];
            const seekTime = seekTo[cameraKey] || 0;
            if (player && seekTime > 0) {
                player.seekTo(seekTime, 'seconds');
            }
        },
        [seekTo]
    );
    const handleError = useCallback(
        (cameraKey: string, error: any) => {
            if (bufferTimeouts.current[cameraKey] !== null) {
                clearTimeout(bufferTimeouts.current[cameraKey]!);
                bufferTimeouts.current[cameraKey] = null;
            }
            setVideoErrors(prev => ({ ...prev, [cameraKey]: '' }));
            setVideoLoading(prev => ({ ...prev, [cameraKey]: false }));
        },
        []
    );
    const handleBuffer = useCallback(
        (cameraKey: string) => {
            if (bufferTimeouts.current[cameraKey] !== null) {
                clearTimeout(bufferTimeouts.current[cameraKey]!);
            }
            bufferTimeouts.current[cameraKey] = setTimeout(() => {
                setVideoLoading(prev => ({ ...prev, [cameraKey]: true }));
            }, 300);
        },
        []
    );
    const handleBufferEnd = useCallback(
        (cameraKey: string) => {
            if (bufferTimeouts.current[cameraKey] !== null) {
                clearTimeout(bufferTimeouts.current[cameraKey]!);
                bufferTimeouts.current[cameraKey] = null;
            }
            setVideoLoading(prev => ({ ...prev, [cameraKey]: false }));
        },
        []
    );
    const togglePlayPause = useCallback(() => {
        setIsPlaying(prev => !prev);
    }, []);
    const toggleSingleVideoFullscreen = useCallback(
        async (index: number) => {
            const cameraKey = `video-${index}`;
            const videoContainer = videoContainerRefs.current[cameraKey];
            if (!videoContainer) return;
            if (selectedCamera === index) {
                if (document.fullscreenElement) {
                    try {
                        await document.exitFullscreen();
                    } catch (err) {
                        console.error('Error exiting fullscreen:', err);
                    }
                }
                setSelectedCamera(null);
            } else {
                setSelectedCamera(index);
                try {
                    await videoContainer.requestFullscreen();
                } catch (err) {
                    console.error('Error entering fullscreen:', err);
                    setSelectedCamera(null);
                }
            }
        },
        [selectedCamera]
    );
    const toggleFullscreen = useCallback(async () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) {
            try {
                await containerRef.current.requestFullscreen();
            } catch (err) {
                console.error('Fullscreen error:', err);
            }
        } else {
            try {
                debouncedHandleTimelineChange()
                await document.exitFullscreen();
            } catch (err) {
                console.error('Error exiting fullscreen:', err);
            }
        }
    }, []);
    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) {
                setSelectedCamera(null);
            }
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);
    const handlePinClick = (index: number) => {
        const newCameraOrder = [...cameraOrder];
        const currentIndex = newCameraOrder.indexOf(index);
        if (currentIndex === -1 || cameraOrder.length <= 1) return;
        newCameraOrder.splice(currentIndex, 1);
        newCameraOrder.push(index);
        setCameraOrder(newCameraOrder);
    };
    const videoCount = useMemo(() => videoSegments.length || 1, [videoSegments]);
    const isOdd = useMemo(() => videoCount % 2 !== 0, [videoCount]);
    const isSingle = useMemo(() => videoCount === 1, [videoCount]);
    const handleRotateVideo = (cameraKey: string) => {
        setVideoRotation(prev => ({
            ...prev,
            [cameraKey]: (prev[cameraKey] || 0) + 90,
        }));
    };
    const startRecording = (cameraKey: string) => {
        const videoElement: any = playerRefs.current[cameraKey]?.getInternalPlayer() as HTMLVideoElement;
        const rotation = videoRotation[cameraKey] || 0;
        if (!videoElement) {
            showToast("Could not access the internal video element.", "e");
            return;
        }
        if (videoElement.readyState < 3) {
            showToast("Please start playing the video first before recording.", "e");
            return;
        }
        if (typeof videoElement.captureStream !== "function") {
            showToast("Video recording is not supported by your browser.", "e");
            return;
        }
        // Create canvas to apply rotation
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        const width = videoElement.videoWidth;
        const height = videoElement.videoHeight;
        // Adjust canvas size and rotation based on degree
        if (rotation === 90 || rotation === 270) {
            canvas.width = height;
            canvas.height = width;
        } else {
            canvas.width = width;
            canvas.height = height;
        }
        const drawFrame = () => {
            ctx.save();
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            // Apply rotation
            switch (rotation) {
                case 90:
                    ctx.translate(canvas.width, 0);
                    ctx.rotate(Math.PI / 2);
                    break;
                case 180:
                    ctx.translate(canvas.width, canvas.height);
                    ctx.rotate(Math.PI);
                    break;
                case 270:
                    ctx.translate(0, canvas.height);
                    ctx.rotate((3 * Math.PI) / 2);
                    break;
                default:
                    break;
            }
            ctx.drawImage(videoElement, 0, 0, width, height);
            ctx.restore();
            requestAnimationFrame(drawFrame);
        };
        drawFrame();
        const stream = canvas.captureStream(30); // 30fps
        const recorder = new MediaRecorder(stream);
        recorderRefs.current[cameraKey] = recorder;
        chunksRefs.current[cameraKey] = [];
        recorder.ondataavailable = (e) => {
            if (e.data.size > 0) chunksRefs.current[cameraKey].push(e.data);
        };
        recorder.onstop = () => {
            const blob = new Blob(chunksRefs.current[cameraKey], { type: 'video/webm' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `recording-${cameraKey}.webm`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            if (intervalRefs.current[cameraKey]) {
                clearInterval(intervalRefs.current[cameraKey]!);
                intervalRefs.current[cameraKey] = null;
            }
            setRecordingTimes((prev) => ({ ...prev, [cameraKey]: 0 }));
            setRecordedUrls((prev) => {
                const updated = { ...prev };
                delete updated[cameraKey];
                return updated;
            });
        };
        recorder.start();
        setRecordingState((prev) => ({ ...prev, [cameraKey]: true }));
        setRecordingTimes((prev) => ({ ...prev, [cameraKey]: 0 }));
        intervalRefs.current[cameraKey] = setInterval(() => {
            setRecordingTimes((prev) => ({
                ...prev,
                [cameraKey]: (prev[cameraKey] || 0) + 1,
            }));
        }, 1000);
    };
    const stopRecording = (cameraKey: string) => {
        const recorder = recorderRefs.current[cameraKey];
        if (recorder) {
            recorder.stop();
            setRecordingState((prev) => ({ ...prev, [cameraKey]: false }));
            if (intervalRefs.current[cameraKey]) {
                clearInterval(intervalRefs.current[cameraKey]!);
                intervalRefs.current[cameraKey] = null;
            }
        }
    };
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
        const secs = (seconds % 60).toString().padStart(2, '0');
        return `${mins}:${secs}`;
    };
    // console.log("currentTime",currentTime);

    const dispatch = useDispatch();
    const { activeIndex } = useSelector((state: any) => state.service);

    const onClickPlayBack = () => {
        dispatch(setActiveIndex(0));
    }




    return (
        <Row>
            {activeIndex === 1 && (
                <div
                    onClick={onClickPlayBack}
                    style={{
                        position: 'absolute',
                        zIndex: 1,
                        gap: 4,
                        margin: "5px 20px",
                        padding: 5,
                        backgroundColor: colors.modalBackground,
                        color: colors.white,
                        fontWeight: 'bold',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        width: "120px"
                    }}
                >
                    <ArrowBack style={{ marginLeft: 5, color: colors.orange }} />
                    Playback
                </div>
            )}
            {videoSegments.length > 0 ? <Col xs={12}>
                <div ref={containerRef} className="video-player-container">
                    <div ref={videoSectionRef} className={`video-grid ${isSingle ? 'single' : ''} ${isOdd ? 'odd' : 'even'}`}>
                        {cameraOrder.map((index, displayIndex) => {
                            const cameraKey = `video-${index}`;
                            if (selectedCamera !== null && selectedCamera !== index) return null;
                            const segment = findVideoSegment(currentTime, index);
                            return (
                                <div
                                    key={cameraKey}
                                    ref={el => (videoContainerRefs.current[cameraKey] = el)}
                                    className="video-wrapper"
                                    style={{ position: 'relative' }}
                                >
                                    {segment && !isSDCard ? (
                                        <>
                                            <div // New wrapper for relative positioning
                                                className="player-container"
                                                onMouseEnter={() => setIsHovered(true)}
                                                onMouseLeave={() => setIsHovered(false)}
                                            >
                                                <ReactPlayer
                                                    ref={el => (playerRefs.current[cameraKey] = el)}
                                                    url={videoUrls[cameraKey]}
                                                    playing={isPlaying}
                                                    playbackRate={playbackRate}
                                                    volume={displayIndex === cameraOrder.length - 1 ? volume : 0}
                                                    muted={displayIndex === cameraOrder.length - 1 ? muted : true}
                                                    width="100%"
                                                    height="100%"
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        transform: `rotate(${videoRotation[cameraKey] || 0}deg)`,
                                                        transformOrigin: 'center center',
                                                    }}
                                                    config={{
                                                        file: {
                                                            attributes: {
                                                                style: {
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: videoSegments.length === 2 ? 'fill' : 'cover',
                                                                },
                                                                disablePictureInPicture: true,
                                                            },
                                                            hlsOptions: {
                                                                maxBufferLength: 60,
                                                                maxMaxBufferLength: 120,
                                                                liveSyncDurationCount: 3,
                                                                liveMaxLatencyDurationCount: 5,
                                                                enableWorker: true,
                                                                fragLoadingTimeOut: 20000,
                                                                manifestLoadingTimeOut: 15000,
                                                                autoStartLoad: true,
                                                            },
                                                        },
                                                    }}
                                                    controls={false}
                                                    onReady={() => handleReady(cameraKey)}
                                                    onError={e => handleError(cameraKey, e)}
                                                    onBuffer={() => handleBuffer(cameraKey)}
                                                    onBufferEnd={() => handleBufferEnd(cameraKey)}
                                                />
                                                {/* Per-video loader overlay when loading */}
                                                {videoLoading[cameraKey] && (
                                                    <div className="loader-overlay">
                                                        <div className="loader"></div>
                                                    </div>
                                                )}
                                                {/* Per-video error overlay */}
                                                {videoErrors[cameraKey] && (
                                                    <div className="error-overlay">
                                                        {videoErrors[cameraKey]}
                                                    </div>
                                                )}
                                            </div>
                                            {isHovered && videoSegments.length >= 3 && (
                                                <button
                                                    onClick={() => handlePinClick(index)}
                                                    style={{
                                                        zIndex: 1000,
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        borderRadius: '10px',
                                                        padding: '5px 10px',
                                                        cursor: cameraOrder.length > 1 ? 'pointer' : 'not-allowed',
                                                        color: 'red',
                                                    }}
                                                    disabled={cameraOrder.length <= 1}
                                                >
                                                    <PushPinIcon style={{ color: 'white', fontSize: 24 }} />
                                                </button>
                                            )}
                                            {displayIndex !== cameraOrder.length - 1 ? null : (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        bottom: '10px',
                                                        left: '10px',
                                                        display: 'flex',
                                                        gap: '10px',
                                                        zIndex: 10,
                                                        background: 'rgba(0, 0, 0, 0.5)',
                                                        padding: '5px',
                                                        borderRadius: '5px',
                                                    }}
                                                >
                                                    <button
                                                        onClick={togglePlayPause}
                                                        disabled={isJumping}
                                                        style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
                                                    >
                                                        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                                                    </button>
                                                    <button
                                                        onClick={toggleFullscreen}
                                                        disabled={isJumping}
                                                        style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
                                                    >
                                                        <AspectRatioIcon />
                                                    </button>
                                                    <button
                                                        onClick={() => setMuted(!muted)}
                                                        disabled={isJumping}
                                                        style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
                                                    >
                                                        {muted ? <VolumeOff className="volume-icon" /> : <VolumeUpIcon className="volume-icon" />}
                                                    </button>
                                                    <button
                                                        onClick={() => setShowSpeedOptions(!showSpeedOptions)}
                                                        style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
                                                    >
                                                        {/* <SlowMotionVideoIcon titleAccess="Click To Set Video Playback Speed" /> */}
                                                        <p style={{ margin: "0px", fontWeight: "bold" }}>{playbackRate}X</p>
                                                    </button>
                                                    {showSpeedOptions && (
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                bottom: '50px',
                                                                left: '10px',
                                                                background: 'rgba(0, 0, 0, 0.7)',
                                                                padding: '8px',
                                                                borderRadius: '5px',
                                                                display: 'flex',
                                                                gap: '10px',
                                                                zIndex: 1000,
                                                            }}
                                                        >
                                                            {videoPlaySec?.map(speed => (
                                                                <button
                                                                    key={speed}
                                                                    onClick={() => {
                                                                        setPlaybackRate(speed);
                                                                        setShowSpeedOptions(false);
                                                                    }}
                                                                    style={{
                                                                        background: playbackRate === speed ? 'white' : 'transparent',
                                                                        color: playbackRate === speed ? 'black' : 'white',
                                                                        border: 'none',
                                                                        borderRadius: '3px',
                                                                        padding: '5px 8px',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                >
                                                                    {speed}x
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="no-video" style={{ textAlign: "center" }}>
                                            {displayIndex === cameraOrder.length - 1 && !isSDCard && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        bottom: '10px',
                                                        left: '10px',
                                                        display: 'flex',
                                                        gap: '10px',
                                                        zIndex: 10,
                                                        background: 'rgba(0, 0, 0, 0.5)',
                                                        padding: '5px',
                                                        borderRadius: '5px',
                                                    }}
                                                >
                                                    <button
                                                        onClick={togglePlayPause}
                                                        disabled={isJumping}
                                                        style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
                                                    >
                                                        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                                                    </button>
                                                    <button
                                                        onClick={toggleFullscreen}
                                                        disabled={isJumping}
                                                        style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
                                                    >
                                                        <AspectRatioIcon />
                                                    </button>
                                                    <button
                                                        onClick={() => setMuted(!muted)}
                                                        disabled={isJumping}
                                                        style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
                                                    >
                                                        {muted ? <VolumeOff className="volume-icon" /> : <VolumeUpIcon className="volume-icon" />}
                                                    </button>
                                                </div>
                                            )}
                                            {isSDCard
                                                ? 'Video not available on server.'
                                                : 'No video available.'}
                                        </div>
                                    )}
                                    {(segment !== undefined && !isSDCard) ?
                                        <>
                                            <div style={{
                                                position: 'absolute',
                                                left: videoSegments.length === 2 ? '4%' : '5%',
                                                top: '29%',
                                                transform: 'translate(-50%, -50%)',
                                                zIndex: 10,
                                                background: "rgba(0, 0, 0, 0.5)",
                                                borderRadius: "10px",
                                                lineHeight: "32px",
                                            }}>
                                                <button
                                                >
                                                    <CropFreeIcon titleAccess="Click To Full Screen" onClick={() => toggleSingleVideoFullscreen(index)} style={{ color: 'red' }} />
                                                </button>
                                                <br />
                                                <button
                                                >
                                                    <AutorenewIcon titleAccess="Click To Rotate Video" onClick={() => handleRotateVideo(cameraKey)} style={{ color: 'red' }} />
                                                </button>
                                                <br />
                                                {!recordingState[cameraKey] && !recordedUrls[cameraKey] && (
                                                    <button onClick={() => startRecording(cameraKey)}>
                                                        <FiberManualRecordIcon style={{ color: 'red' }} titleAccess="Click To Start Recording" />
                                                    </button>
                                                )}
                                                {recordingState[cameraKey] && (
                                                    <button onClick={() => stopRecording(cameraKey)}>
                                                        <StopIcon titleAccess="Click To Stop Recording" />
                                                    </button>
                                                )}
                                                {recordedUrls[cameraKey] && (
                                                    <a href={recordedUrls[cameraKey]!} download={`recording-${cameraKey}.webm`}>
                                                        <button>
                                                            <CloudDownloadIcon titleAccess="Click To Download Recorded Video" style={{ color: "#fff" }} />
                                                        </button>
                                                    </a>
                                                )}
                                            </div>
                                            {recordingState[cameraKey] && (
                                                <div style={{ color: 'red', fontWeight: 'bold', zIndex: 999, position: "absolute", padding: "0px 20px" }}>
                                                    Recording: {formatTime(recordingTimes[cameraKey] || 0)}
                                                </div>
                                            )}
                                        </>
                                        : ''}
                                </div>
                            );
                        })}
                    </div>
                    <div className="timeline-wrapper">
                        <ProgressScale
                            key={`${liveVideoSegments}`}
                            value={currentTime}
                            segments={liveVideoSegments}
                            onChange={handleTimelineChange}
                        />
                    </div>
                    <div className="stepper-wrapper">
                        <Stepper
                            onClick={(data: any) => jumpToTime(data.start_time)}
                            steps={timeFilterData}
                        />
                    </div>
                </div>
            </Col> : <div
                style={{
                    width: '100%',
                    height: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'visible',
                }}
            >
                <div>Playback video not found</div>
            </div>
            }
        </Row>
    );
};
export default React.memo(MultiVideoPlayerLiveCctv);